import { apiClient } from '../../services/apiClient';
import type { UserProfile, UpdateProfileRequest, ChangeEmailRequest } from './types';

/**
 * 現在のユーザーのプロフィールを取得
 */
export async function getCurrentUserProfile(): Promise<UserProfile> {
  const response = await apiClient.get('/users/me/profile');
  return response.data;
}

/**
 * プロフィールを更新
 */
export async function updateProfile(data: UpdateProfileRequest): Promise<UserProfile> {
  const response = await apiClient.patch('/users/me/profile', data);
  return response.data.user;
}

/**
 * メールアドレス変更をリクエスト（確認メールが送信される）
 */
export async function requestEmailChange(userId: string, data: ChangeEmailRequest): Promise<void> {
  await apiClient.patch(`/users/${userId}/email`, data);
}

/**
 * プロフィール画像をアップロード
 */
export async function uploadProfilePicture(file: File): Promise<{ picture_url: string; user: UserProfile }> {
  const formData = new FormData();
  formData.append('file', file);

  const response = await apiClient.post('/users/me/picture', formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });
  return response.data;
}

/**
 * プロフィール画像を削除
 */
export async function deleteProfilePicture(): Promise<{ user: UserProfile }> {
  const response = await apiClient.delete('/users/me/picture');
  return response.data;
}

/**
 * クロップ済み画像をアップロード（Base64）
 */
export async function uploadCroppedProfilePicture(base64Data: string): Promise<{ picture_url: string; user: UserProfile }> {
  // Base64をBlobに変換（JPEG形式）
  const byteCharacters = atob(base64Data);
  const byteNumbers = new Array(byteCharacters.length);
  for (let i = 0; i < byteCharacters.length; i++) {
    byteNumbers[i] = byteCharacters.charCodeAt(i);
  }
  const byteArray = new Uint8Array(byteNumbers);
  const blob = new Blob([byteArray], { type: 'image/jpeg' });
  const file = new File([blob], 'profile.jpg', { type: 'image/jpeg' });

  return uploadProfilePicture(file);
}

// =====================
// Full Body Photos API
// =====================

export type GeneratedActorInfo = {
  id: string;
  display_name: string;
};

export type FullBodyPhoto = {
  id: string;
  url: string;
  thumbnail_url: string;
  created_at: string;
  generated_actors?: GeneratedActorInfo[];  // 複数のAI俳優に対応
};

/**
 * 全身写真の一覧を取得
 */
export async function getFullBodyPhotos(): Promise<FullBodyPhoto[]> {
  const response = await apiClient.get('/users/me/full-body-photos');
  return response.data.photos;
}

/**
 * 全身写真をアップロード
 */
export async function uploadFullBodyPhoto(file: File): Promise<FullBodyPhoto> {
  const formData = new FormData();
  formData.append('file', file);

  const response = await apiClient.post('/users/me/full-body-photos', formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });
  return response.data.photo;
}

/**
 * 全身写真を削除
 */
export async function deleteFullBodyPhoto(photoId: string): Promise<void> {
  await apiClient.delete(`/users/me/full-body-photos/${photoId}`);
}

/**
 * セーフモード設定を更新
 */
export async function updateSafeMode(safe_mode: boolean): Promise<{ success: boolean; safe_mode: boolean }> {
  const response = await apiClient.patch('/users/me/safe-mode', { safe_mode });
  return response.data;
}

/**
 * ヘルプツールチップ表示設定を更新
 */
export async function updateShowHelpTooltips(
  show_help_tooltips: boolean
): Promise<{ success: boolean; show_help_tooltips: boolean }> {
  const response = await apiClient.patch('/users/me/help-tooltips', { show_help_tooltips });
  return response.data;
}

/**
 * ナビゲーションガイド完了を記録
 */
export async function markNavigationGuideComplete(): Promise<{
  success: boolean;
  navigation_guide_completed_at: string;
}> {
  const response = await apiClient.post('/users/me/navigation-guide-complete');
  return response.data;
}

/**
 * ナビゲーションガイドの完了状態をリセット (再生用)
 */
export async function resetNavigationGuide(): Promise<{
  success: boolean;
  navigation_guide_completed_at: null;
}> {
  const response = await apiClient.post('/users/me/navigation-guide-reset');
  return response.data;
}
